import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Star, ChevronRight, Check, Bell, Plus, ShoppingBag, ShieldCheck } from "lucide-react";
import { api, RON, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import RecentlyViewed from "@/components/RecentlyViewed";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import { recordView } from "@/lib/recentlyViewed";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [reviewsData, setReviewsData] = useState({ average: 0, count: 0, distribution: {}, reviews: [] });
  const [reviewForm, setReviewForm] = useState({ rating: 0, title: "", comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false);
  const { add } = useCart();
  const { user, toggleWishlist } = useAuth();

  const loadReviews = () => api.get(`/products/${slug}/reviews`).then(({ data }) => setReviewsData(data));

  useEffect(() => {
    api.get(`/products/${slug}`).then(({ data }) => {
      setProduct(data);
      setActive(0);
      setQty(1);
    });
    loadReviews();
    setReviewForm({ rating: 0, title: "", comment: "" });
    recordView(slug);
  }, [slug]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Autentifică-te pentru a lăsa o recenzie");
    if (!reviewForm.rating) return toast.error("Alege o notă (1-5 stele)");
    if (!reviewForm.comment.trim()) return toast.error("Scrie un comentariu");
    setSubmittingReview(true);
    try {
      const { data } = await api.post("/reviews", { product_slug: slug, ...reviewForm });
      toast.success(data.message);
      setReviewForm({ rating: 0, title: "", comment: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmittingReview(false);
    }
  };

  if (!product) return <div className="pt-40 pb-40 text-center text-muted-foreground">Se încarcă produsul...</div>;

  const gram = product.grammages[product.grammages.length - 1];
  const out = product.stock_status === "out";
  const inWishlist = user?.wishlist?.includes(product.slug);

  const addToCart = () => {
    if (out) return;
    add(product, gram, qty);
    toast.success(`${product.name} adăugat în coș`, { description: `${product.grammage} · ${qty} buc.` });
  };

  const wish = async () => {
    if (!user) return toast.error("Autentifică-te pentru a salva favorite");
    const a = await toggleWishlist(product.slug);
    toast[a ? "success" : "message"](a ? "Adăugat la favorite" : "Șters din favorite");
  };

  const notify = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/stock/notify", { product_id: product.slug, email: notifyEmail });
      toast.success(data.message);
      setNotifyEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const specs = [
    ["Brand", product.brand], ["Tip", product.type], ["Gramaj", product.grammage],
    ["Compoziție", product.composition], ["Origine", product.origin],
    ["Prăjire", product.roast], ["Intensitate", `${product.intensity}/${product.intensity_scale || 13}`],
    ["Aciditate", product.acidity], ["Note aromatice", product.aroma_notes],
    ["Preparare", product.brewing], ["Compatibilitate", product.compatibility],
    ["Producător", product.producer], ["Păstrare", product.storage],
    ["SKU", product.sku],
  ].filter(([, v]) => v);

  return (
    <div className="pt-20">
      <Seo
        title={`${product.brand} ${product.name} ${product.grammage}`}
        description={product.description}
        image={product.image}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: `${product.brand} ${product.name}`,
            image: (product.gallery && product.gallery.length ? product.gallery : [product.image]).filter(Boolean),
            description: product.description,
            sku: product.sku || product.slug,
            brand: { "@type": "Brand", name: product.brand },
            ...(product.reviews > 0 && product.rating
              ? { aggregateRating: { "@type": "AggregateRating", ratingValue: product.rating, reviewCount: product.reviews } }
              : {}),
            offers: {
              "@type": "Offer",
              url: window.location.origin + window.location.pathname,
              priceCurrency: "RON",
              price: product.effective_price,
              availability: out ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Acasă", item: window.location.origin + "/" },
              { "@type": "ListItem", position: 2, name: "Produse", item: window.location.origin + "/produse" },
              { "@type": "ListItem", position: 3, name: product.name, item: window.location.origin + window.location.pathname },
            ],
          },
        ]}
      />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        <nav className="flex items-center gap-2 text-xs text-muted-foreground mb-10" data-testid="breadcrumb">
          <Link to="/" className="hover:text-roast">Acasă</Link>
          <ChevronRight size={13} />
          <Link to="/produse" className="hover:text-roast">Produse</Link>
          <ChevronRight size={13} />
          <span className="text-ink">{product.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* gallery */}
          <div className="space-y-4">
            <motion.div key={active} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
              className="relative aspect-[4/5] bg-stone overflow-hidden">
              <img src={product.gallery[active] || product.image} alt={product.name} className="h-full w-full object-cover" data-testid="product-main-image" />
              {product.on_sale && (
                <span className="absolute top-5 left-5 bg-roast text-cream text-sm font-semibold px-3 py-1.5">Reducere −{product.discount_percent}%</span>
              )}
            </motion.div>
            {product.gallery.length > 1 && (
              <div className="flex gap-4">
                {product.gallery.map((g, i) => (
                  <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
                    className={`h-24 w-20 bg-stone overflow-hidden border-2 transition-colors ${active === i ? "border-roast" : "border-transparent"}`}>
                    <img src={g} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* info */}
          <div>
            <Reveal>
              <p className="overline text-roast">{product.brand}</p>
              <h1 className="font-display text-5xl md:text-6xl tracking-tight mt-2">{product.name}</h1>
              <div className="flex items-center gap-2 mt-4">
                <div className="flex text-sand">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={15} fill={i < Math.round(product.rating || 0) ? "currentColor" : "none"} />
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">{product.reviews || 0} recenzii</span>
              </div>

              <div className="flex items-baseline gap-3 mt-6" data-testid="product-price">
                {product.on_sale && <span className="text-xl text-muted-foreground line-through">{RON(product.old_price)}</span>}
                <span className={`text-3xl font-display ${product.on_sale ? "text-roast" : ""}`}>{RON(product.effective_price)}</span>
                {product.on_sale && <span className="text-sm bg-roast/10 text-roast px-2 py-0.5 font-medium">Economisești {RON(product.old_price - product.effective_price)}</span>}
              </div>
              <p className="text-xs text-muted-foreground">Preț final</p>

              {/* stock */}
              <div className="mt-4" data-testid="stock-status">
                {out ? (
                  <span className="inline-flex items-center gap-2 text-sm text-destructive font-medium">● Stoc epuizat</span>
                ) : product.stock_status === "limited" ? (
                  <span className="inline-flex items-center gap-2 text-sm text-roast font-medium">● Stoc limitat – ultimele {product.stock} buc.</span>
                ) : (
                  <span className="inline-flex items-center gap-2 text-sm text-green-700 font-medium">● În stoc</span>
                )}
              </div>

              <p className="text-muted-foreground leading-relaxed mt-6">{product.description}</p>

              {/* actions */}
              {out ? (
                <form onSubmit={notify} className="mt-8 border border-line p-5" data-testid="notify-form">
                  <p className="flex items-center gap-2 font-medium"><Bell size={16} className="text-roast" /> Anunță-mă când revine în stoc</p>
                  <div className="flex gap-3 mt-4">
                    <input required type="email" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)}
                      placeholder="Adresa ta de email" data-testid="notify-email"
                      className="flex-1 bg-surface border border-line px-4 py-3 outline-none focus:border-roast transition-colors" />
                    <button type="submit" data-testid="notify-submit" className="bg-ink text-cream px-6 hover:bg-roast transition-colors text-sm font-semibold">Anunță-mă</button>
                  </div>
                </form>
              ) : (
                <div className="flex items-stretch gap-3 mt-8">
                  <div className="flex items-center border border-line">
                    <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4" aria-label="minus">−</button>
                    <span className="w-10 text-center" data-testid="qty-value">{qty}</span>
                    <button onClick={() => setQty((q) => Math.min(product.stock, q + 1))} className="px-4" aria-label="plus">+</button>
                  </div>
                  <button onClick={addToCart} data-testid="add-to-cart-btn"
                    className="flex-1 bg-ink text-cream py-4 hover:bg-roast transition-colors duration-300 text-sm font-semibold tracking-wide">
                    Adaugă în coș
                  </button>
                  <button onClick={wish} data-testid="detail-wishlist-btn"
                    className={`px-5 border transition-colors ${inWishlist ? "bg-roast text-cream border-roast" : "border-line hover:border-ink"}`} aria-label="Favorite">
                    <Heart size={18} fill={inWishlist ? "currentColor" : "none"} />
                  </button>
                </div>
              )}

              {!out && (
                <div className="flex items-center gap-2 mt-6 text-sm text-muted-foreground">
                  <Check size={16} className="text-roast" /> Livrare 1–3 zile lucrătoare
                </div>
              )}

              {/* specs */}
              <dl className="grid grid-cols-2 gap-x-8 sm:gap-x-12 gap-y-4 mt-10 border-t border-line pt-8">
                {specs.map(([label, value]) => (
                  <div key={label} className="min-w-0">
                    <dt className="overline text-ink/40 break-words">{label}</dt>
                    <dd className="mt-1 text-sm capitalize break-words">{value}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          </div>
        </div>

        {/* Reviews */}
        <div className="mt-28 border-t border-line pt-16" data-testid="reviews-section" id="recenzii">
          <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-12">Recenzii</h2>
          <div className="grid lg:grid-cols-[320px_1fr] gap-12 lg:gap-20">
            {/* aggregate + form */}
            <div>
              <div className="flex items-end gap-4">
                <span className="font-display text-6xl leading-none" data-testid="reviews-average">{reviewsData.average || 0}</span>
                <div className="pb-1">
                  <span className="flex text-roast">
                    {[...Array(5)].map((_, i) => <Star key={i} size={16} fill={i < Math.round(reviewsData.average) ? "currentColor" : "none"} />)}
                  </span>
                  <p className="text-xs text-muted-foreground mt-1" data-testid="reviews-count">{reviewsData.count} recenzii</p>
                </div>
              </div>
              <div className="space-y-1.5 mt-6">
                {[5, 4, 3, 2, 1].map((n) => {
                  const c = reviewsData.distribution?.[n] || 0;
                  const pct = reviewsData.count ? (c / reviewsData.count) * 100 : 0;
                  return (
                    <div key={n} className="flex items-center gap-3 text-xs">
                      <span className="w-3 text-muted-foreground">{n}</span>
                      <Star size={11} className="text-roast" fill="currentColor" />
                      <div className="flex-1 h-1.5 bg-stone overflow-hidden"><div className="h-full bg-roast" style={{ width: `${pct}%` }} /></div>
                      <span className="w-5 text-right text-muted-foreground">{c}</span>
                    </div>
                  );
                })}
              </div>

              {/* form */}
              <div className="mt-10 border-t border-line pt-8">
                <p className="overline text-ink/50 mb-4">Lasă o recenzie</p>
                {user ? (
                  <form onSubmit={submitReview} className="space-y-4" data-testid="review-form">
                    <div className="flex gap-1" data-testid="review-star-select">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button key={n} type="button" onClick={() => setReviewForm((f) => ({ ...f, rating: n }))}
                          data-testid={`review-star-${n}`} aria-label={`${n} stele`} className="text-roast hover:scale-110 transition-transform">
                          <Star size={26} fill={n <= reviewForm.rating ? "currentColor" : "none"} />
                        </button>
                      ))}
                    </div>
                    <input value={reviewForm.title} onChange={(e) => setReviewForm((f) => ({ ...f, title: e.target.value }))}
                      placeholder="Titlu (opțional)" data-testid="review-title"
                      className="w-full bg-surface border border-line px-4 py-3 text-sm outline-none focus:border-roast transition-colors" />
                    <textarea rows={4} value={reviewForm.comment} onChange={(e) => setReviewForm((f) => ({ ...f, comment: e.target.value }))}
                      placeholder="Spune-ne părerea ta despre acest produs..." data-testid="review-comment"
                      className="w-full bg-surface border border-line px-4 py-3 text-sm outline-none focus:border-roast transition-colors resize-none" />
                    <button type="submit" disabled={submittingReview} data-testid="review-submit"
                      className="w-full bg-ink text-cream py-3.5 text-sm font-semibold tracking-wide hover:bg-roast transition-colors disabled:opacity-60">
                      {submittingReview ? "Se trimite..." : "Trimite recenzia"}
                    </button>
                    <p className="text-xs text-muted-foreground">Recenzia va apărea după verificarea echipei noastre.</p>
                  </form>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    <Link to="/autentificare" className="text-roast underline">Autentifică-te</Link> pentru a lăsa o recenzie.
                  </p>
                )}
              </div>
            </div>

            {/* list */}
            <div className="space-y-8" data-testid="reviews-list">
              {reviewsData.reviews.length === 0 && (
                <p className="text-muted-foreground">Fii primul care lasă o recenzie pentru acest produs.</p>
              )}
              {reviewsData.reviews.map((r, i) => (
                <div key={i} className="border-b border-line pb-8 last:border-0" data-testid="review-item">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="flex text-roast">
                      {[...Array(5)].map((_, j) => <Star key={j} size={14} fill={j < r.rating ? "currentColor" : "none"} />)}
                    </span>
                    <span className="font-medium text-sm">{r.user_name}</span>
                    {r.verified_purchase && (
                      <span className="inline-flex items-center gap-1 text-xs text-roast bg-roast/10 px-2 py-0.5" data-testid="review-verified-badge">
                        <ShieldCheck size={12} /> Achiziție verificată
                      </span>
                    )}
                  </div>
                  {r.title && <p className="font-medium mt-3">{r.title}</p>}
                  <p className="text-muted-foreground leading-relaxed mt-1">{r.comment}</p>
                  <p className="text-xs text-ink/40 mt-3">{new Date(r.created_at).toLocaleDateString("ro-RO")}</p>
                  {r.admin_reply && (
                    <div className="mt-4 ml-4 md:ml-8 border-l-2 border-roast bg-roast/5 pl-4 py-3" data-testid="review-admin-reply">
                      <p className="text-xs font-semibold text-roast uppercase tracking-wide">Răspuns Coffee Cup Supply</p>
                      <p className="text-sm text-ink/80 leading-relaxed mt-1">{r.admin_reply}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Frequently bought together — Admin-configured combinations */}
        {product.fbt?.length > 0 && (
          <div className="mt-28" data-testid="bought-together">
            <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-10">Cumpărate frecvent împreună</h2>
            <div className="space-y-6">
              {product.fbt.map((combo) => (
                <FbtBundle key={combo.id} combo={combo} add={add} />
              ))}
            </div>
          </div>
        )}

        {product.similar?.length > 0 && (
          <div className="mt-28" data-testid="similar-products">
            <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-12">Produse similare</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {product.similar.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </div>
          </div>
        )}

        <RecentlyViewed exclude={slug} />
      </div>
    </div>
  );
}

function FbtBundle({ combo, add }) {
  const [adding, setAdding] = useState(false);
  const gramOf = (p) => p.grammages[p.grammages.length - 1];
  const purchasable = combo.products.filter((p) => p.stock_status !== "out");
  const canAdd = purchasable.length >= 1;

  const addAll = () => {
    if (adding || !canAdd) return;
    setAdding(true);
    purchasable.forEach((p) => add(p, gramOf(p), 1));
    toast.success("Produse adăugate în coș", { description: `${purchasable.length} ${purchasable.length === 1 ? "produs" : "produse"}` });
    setTimeout(() => setAdding(false), 900);
  };

  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-6 bg-stone p-6 md:p-10" data-testid={`fbt-combo-${combo.id}`}>
      <div className="flex flex-wrap items-center gap-4 flex-1">
        {combo.products.map((p, i) => {
          const out = p.stock_status === "out";
          return (
            <div key={p.slug} className="flex items-center gap-4">
              {i > 0 && <Plus size={20} className="text-roast shrink-0" />}
              <Link to={`/produs/${p.slug}`} className={`group flex flex-col items-center text-center w-32 ${out ? "opacity-60" : ""}`} data-testid={`fbt-product-${p.slug}`}>
                <div className="relative aspect-square w-full bg-surface overflow-hidden">
                  <img src={p.image} alt={p.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  {out && <span className="absolute inset-x-0 bottom-0 bg-ink/80 text-cream text-[10px] py-1 tracking-wide text-center">Stoc epuizat</span>}
                </div>
                <p className="text-xs mt-2 leading-tight line-clamp-2">{p.brand} {p.name}</p>
                {p.on_sale ? (
                  <p className="text-xs mt-1"><span className="text-muted-foreground line-through mr-1">{RON(p.old_price)}</span><span className="text-roast font-medium">{RON(p.effective_price)}</span></p>
                ) : (
                  <p className="text-xs text-roast font-medium mt-1">{RON(p.effective_price)}</p>
                )}
              </Link>
            </div>
          );
        })}
      </div>
      <div className="lg:text-right lg:border-l lg:border-line lg:pl-10 shrink-0">
        {canAdd ? (
          <>
            <p className="overline text-ink/50">Total {purchasable.length} {purchasable.length === 1 ? "produs" : "produse"}{purchasable.length < combo.products.length ? " disponibile" : ""}</p>
            <p className="font-display text-3xl mt-1" data-testid="bundle-total">{RON(combo.total)}</p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground max-w-[220px] lg:ml-auto">Toate produsele din acest pachet sunt momentan indisponibile.</p>
        )}
        <button onClick={addAll} disabled={adding || !canAdd} data-testid="add-bundle-btn"
          className="group mt-4 inline-flex items-center justify-center gap-2 bg-ink text-cream px-6 py-3.5 hover:bg-roast transition-colors text-sm font-semibold tracking-wide w-full lg:w-auto disabled:opacity-60 disabled:cursor-not-allowed">
          <ShoppingBag size={16} />
          {adding ? "Se adaugă..." : canAdd ? "Adaugă toate în coș" : "Momentan indisponibil"}
        </button>
      </div>
    </div>
  );
}
